import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Dialog,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import type { IncidentLocationSchema } from '@/services/types/incident-types';

interface IncidentImageCarouselProps {
  location: IncidentLocationSchema | null | undefined;
  isOpen: boolean;
  onClose: () => void;
}

interface ImageSlide {
  label: string;
  url: string;
  direction?: string;
}

/**
 * Build array of available images from location data
 */
const buildImageSlides = (
  location: IncidentLocationSchema | null | undefined,
): ImageSlide[] => {
  if (!location) return [];

  const slides: ImageSlide[] = [];

  if (location.satellite_image_url) {
    slides.push({
      label: 'Satellite',
      url: location.satellite_image_url,
    });
  }

  if (location.streetview_image_url_0) {
    slides.push({
      label: 'North',
      url: location.streetview_image_url_0,
      direction: 'Facing North (0)',
    });
  }

  if (location.streetview_image_url_90) {
    slides.push({
      label: 'East',
      url: location.streetview_image_url_90,
      direction: 'Facing East (90)',
    });
  }

  if (location.streetview_image_url_180) {
    slides.push({
      label: 'South',
      url: location.streetview_image_url_180,
      direction: 'Facing South (180)',
    });
  }

  if (location.streetview_image_url_270) {
    slides.push({
      label: 'West',
      url: location.streetview_image_url_270,
      direction: 'Facing West (270)',
    });
  }

  return slides;
};

/**
 * Image carousel dialog for viewing all location images
 */
export const IncidentImageCarousel = ({
  location,
  isOpen,
  onClose,
}: IncidentImageCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const slides = useMemo(() => buildImageSlides(location), [location]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        goToPrev();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      }
    },
    [goToPrev, goToNext],
  );

  // Attach keyboard handler when dialog is open
  useEffect(() => {
    if (!isOpen || slides.length === 0) return undefined;

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, slides.length, handleKeyDown]);

  if (slides.length === 0) return null;

  const currentSlide = slides[currentIndex];

  return (
    <Dialog open={isOpen} onClose={onClose} size="4xl">
      <div className="flex items-center justify-between">
        <DialogTitle>Location Images</DialogTitle>
        {location?.google_maps_uri && (
          <a
            href={location.google_maps_uri}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-sm text-blue-600 hover:underline mr-8"
          >
            <ExternalLink className="h-4 w-4" />
            Maps
          </a>
        )}
      </div>

      <DialogBody>
        <div className="space-y-3">
          <div className="flex gap-1">
            {slides.map((slide, index) => (
              <button
                key={slide.label}
                type="button"
                onClick={() => goToSlide(index)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                  index === currentIndex
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                {slide.label}
              </button>
            ))}
          </div>

          <div className="relative aspect-video bg-gray-100 rounded overflow-hidden">
            <img
              src={currentSlide.url}
              alt={`${currentSlide.label} view`}
              className="w-full h-full object-contain"
            />

            {currentSlide.direction && (
              <div className="absolute bottom-3 left-3 bg-black/60 text-white px-2 py-1 rounded text-xs">
                {currentSlide.direction}
              </div>
            )}

            {slides.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={goToPrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full"
                  aria-label="Previous"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={goToNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full"
                  aria-label="Next"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}
          </div>

          {location?.formatted_address && (
            <div className="text-center text-xs text-gray-500">
              {location.formatted_address}
            </div>
          )}
        </div>
      </DialogBody>
    </Dialog>
  );
};
